import { AnyAction, AsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { omit } from "lodash-es";

import { IAPIState, IMessage, IErrorPayload, StateErrorType } from "shared/types";
import { IUser, IAuthActions, AUTH_ACTIONS_PREFIX, AUTH_ACTIONS } from "Auth/types";
import {
  getCurrentUser,
  editCurrentUser,
  login,
  logout,
  getInternalUser,
  editUser,
} from "./actions";

/*
  Meta property stores the status, error and message for each authentication action under their respective keys.
  This way the status of the different authentication action can be accessed individually.
*/
export interface IAuthState {
  currentUser: IUser | null;
  meta: {
    [key in IAuthActions]?: {
      status: IAPIState;
      error?: StateErrorType;
      message?: IMessage;
    };
  };
}

const initialState: IAuthState = {
  currentUser: null,
  meta: {},
};

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;
type PendingAction = ReturnType<GenericAsyncThunk["pending"]>;
type RejectedAction = ReturnType<GenericAsyncThunk["rejected"]>;
type FulfilledAction = ReturnType<GenericAsyncThunk["fulfilled"]>;

const matchPrefix = (action: AnyAction): boolean => action.type.startsWith(AUTH_ACTIONS_PREFIX);

const isPendingAction = (action: AnyAction): action is PendingAction => {
  if (action.type.startsWith(AUTH_ACTIONS.getCurrentUser)) return false;
  return matchPrefix(action) && action.type.endsWith("/pending");
};
const isRejectedAction = (action: AnyAction): action is RejectedAction => {
  if (
    action.type.startsWith(AUTH_ACTIONS.login) ||
    action.type.startsWith(AUTH_ACTIONS.getCurrentUser)
  ) {
    return false;
  }
  return matchPrefix(action) && action.type.endsWith("/rejected");
};
const isFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (
    action.type.startsWith(AUTH_ACTIONS.login) ||
    action.type.startsWith(AUTH_ACTIONS.getCurrentUser) ||
    action.type.startsWith(AUTH_ACTIONS.logout) ||
    action.type.startsWith(AUTH_ACTIONS.getCurrentInternalUser) ||
    action.type.startsWith(AUTH_ACTIONS.editCurrentUser) ||
    action.type.startsWith(AUTH_ACTIONS.editUser)
  ) {
    return false;
  }
  return matchPrefix(action) && action.type.endsWith("/fulfilled");
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearActionMeta: (state, { payload }: PayloadAction<IAuthActions>) => {
      state.meta = omit(state.meta, payload);
    },
  },
  extraReducers: builder => {
    // login
    builder.addCase(login.rejected, (state, { payload }) => {
      state.currentUser = null;
      setStateRejected(state, AUTH_ACTIONS.login, payload);
    });
    builder.addCase(login.fulfilled, (state, { payload }) => {
      state.currentUser = payload;
      setStateFulfilled(state, AUTH_ACTIONS.login);
    });
    // currentUser
    builder.addCase(getCurrentUser.rejected, (state, { type, payload }) => {
      state.currentUser = null;

      const action = type.replace("/rejected", "");
      return setStateRejected(state, action, payload as IErrorPayload);
    });
    builder.addCase(getCurrentUser.fulfilled, (state, { type, payload }) => {
      state.currentUser = payload;

      const action: string = type.replace("/fulfilled", "");
      return setStateFulfilled(state, action, payload as IMessage);
    });
    // get internal user
    builder.addCase(getInternalUser.fulfilled, (state, { payload }) => {
      state.currentUser = payload;
      setStateFulfilled(state, AUTH_ACTIONS.getCurrentInternalUser);
    });
    // edit internal user
    builder.addCase(editCurrentUser.fulfilled, (state, { payload }) => {
      state.currentUser = payload;
      setStateFulfilled(state, AUTH_ACTIONS.editCurrentUser);
    });
    // edit user
    builder.addCase(editUser.fulfilled, (state, { payload }) => {
      setStateFulfilled(state, AUTH_ACTIONS.editUser);
    });
    // logout
    builder.addCase(logout.fulfilled, state => {
      state.currentUser = null;
      setStateFulfilled(state, AUTH_ACTIONS.logout);
    });

    // matchers
    // catches all actions that do not have a custom builder defined above
    builder.addMatcher(isPendingAction, (state, { type }) => {
      const action = type.replace("/pending", "");
      return setStateLoading(state, action);
    });
    builder.addMatcher(isRejectedAction, (state, { type, payload }) => {
      const action = type.replace("/rejected", "");
      return setStateRejected(state, action, payload as IErrorPayload);
    });
    builder.addMatcher(isFulfilledAction, (state, { type, payload }) => {
      const action = type.replace("/fulfilled", "");
      return setStateFulfilled(state, action, payload as IMessage);
    });
  },
});

// Helper methods to set loading, rejected and fulfilled state for an auth request
const setStateLoading = (state: IAuthState, key: string) => {
  state.meta = {
    ...state.meta,
    [key]: { status: IAPIState.pending },
  };
};

const setStateFulfilled = (state: IAuthState, key: string, message?: IMessage) => {
  state.meta = {
    ...state.meta,
    [key]: { status: IAPIState.succeeded, error: null, message },
  };
};

const setStateRejected = (state: IAuthState, key: string, payload: IErrorPayload | undefined) => {
  state.meta = {
    ...state.meta,
    [key]: {
      status: IAPIState.failed,
      error: payload?.error,
      message: payload?.message,
    },
  };
};

export const { clearActionMeta } = authSlice.actions;
export const authReducer = authSlice.reducer;
